import { useCallback, useEffect, useState, useRef } from 'react';
import { Session, SessionUnavailableError } from '@ais-ui/auth';
import { useLocalization } from '@ais-ui/locale-management';
import { useToast } from '@ais-ui/ui-core';
import {
  getCsvVesselTrackResult,
  getKmlVesselTrackResult,
  getQueueStatusOfVesselTrack,
  queueCsvVesselTrack,
  queueKmlVesselTrack,
} from './downloadTrackEndpoints';

export type DownloadStatus =
  | 'idle'
  | 'queued'
  | 'downloading'
  | 'success'
  | 'error';

interface DownloadState {
  status: DownloadStatus;
  error: string | null;
  progress: number;
  data: {
    content: string;
    filename: string;
  } | null;
}

interface UseDownloadTrackResult {
  downloadState: DownloadState;
  startCsvDownload: (mmsi: number) => Promise<void>;
  startKmlDownload: (mmsi: number) => Promise<void>;
  reset: () => void;
}

const POLLING_INTERVAL = 1000; // 1 second

const CONNECTION_LOST_MESSAGE =
  'The connection to the server was lost. Please try again.';

export const useDownloadTrack = (session: Session): UseDownloadTrackResult => {
  const { toast } = useToast();
  const { messages } = useLocalization();
  const downloadedRef = useRef(false);
  // Use refs to avoid stale closures in setInterval callbacks
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);

  const [downloadState, setDownloadState] = useState<DownloadState>({
    status: 'idle',
    error: null,
    progress: 0,
    data: null,
  });

  const clearPollingInterval = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  const reset = useCallback(() => {
    clearPollingInterval();
    downloadedRef.current = false;
    setDownloadState({
      status: 'idle',
      error: null,
      progress: 0,
      data: null,
    });
  }, [clearPollingInterval]);

  const handleError = useCallback(
    (error: Error) => {
      // The transport's own wording ("WebSocket retired: ...") means nothing to
      // the user; the reconnect flow shows the connection state.
      const message =
        error instanceof SessionUnavailableError
          ? CONNECTION_LOST_MESSAGE
          : error.message;

      clearPollingInterval();
      setDownloadState((prev) => ({
        ...prev,
        status: 'error',
        error: message,
      }));

      toast({
        title: messages.genericToastTitleError,
        description: message,
        variant: 'destructive',
      });
    },
    [clearPollingInterval, toast, messages],
  );

  const triggerDownload = useCallback(
    (filename: string, base64Data: string) => {
      if (downloadedRef.current) {
        return; // Prevent multiple downloads
      }

      // Decode the base64 data
      const decodedData = atob(base64Data);

      // Create blob and trigger download
      const blob = new Blob([decodedData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);

      downloadedRef.current = true;
    },
    [],
  );

  const pollStatus = useCallback(
    async (queueUuid: string, fileType: 'kml' | 'csv') => {
      // Skip if a poll is already in-flight or polling was stopped
      if (isPollingRef.current || !pollingIntervalRef.current) {
        return 'QueuePending';
      }
      isPollingRef.current = true;

      try {
        const result = await getQueueStatusOfVesselTrack(session, queueUuid);

        setDownloadState((prev) => ({
          ...prev,
          progress: result.percentComplete,
        }));

        if (result.errorMessage) {
          throw new Error(result.errorMessage);
        }

        if (result.status === 'QueueComplete') {
          clearPollingInterval();
          let downloadResult: { filename: string; data: string };
          if (fileType === 'csv') {
            downloadResult = await getCsvVesselTrackResult(session, queueUuid);
          } else {
            downloadResult = await getKmlVesselTrackResult(session, queueUuid);
          }

          triggerDownload(downloadResult.filename, downloadResult.data);

          setDownloadState((prev) => ({
            ...prev,
            status: 'success',
            data: {
              content: downloadResult.data,
              filename: downloadResult.filename,
            },
          }));

          toast({
            title: messages.genericToastTitleSuccess,
            description: 'Successfully downloaded track',
          });
          return;
        }

        return result.status;
      } catch (error) {
        handleError(error as Error);
      } finally {
        isPollingRef.current = false;
      }
    },
    [
      session,
      clearPollingInterval,
      triggerDownload,
      toast,
      messages,
      handleError,
    ],
  );

  const startCsvDownload = useCallback(
    async (mmsi: number) => {
      try {
        reset();
        setDownloadState((prev) => ({ ...prev, status: 'queued' }));

        toast({
          title: messages.genericToastTitleStarted,
          description: 'Started download of CSV track',
        });

        const { queueUuid } = await queueCsvVesselTrack(session, mmsi);
        setDownloadState((prev) => ({ ...prev, status: 'downloading' }));

        const interval = setInterval(async () => {
          const status = await pollStatus(queueUuid, 'csv');
          if (status !== 'QueuePending') {
            clearPollingInterval();
          }
        }, POLLING_INTERVAL);
        pollingIntervalRef.current = interval;
      } catch (error) {
        handleError(error as Error);
      }
    },
    [
      session,
      reset,
      pollStatus,
      clearPollingInterval,
      handleError,
      toast,
      messages,
    ],
  );

  const startKmlDownload = useCallback(
    async (mmsi: number) => {
      try {
        reset();
        setDownloadState((prev) => ({ ...prev, status: 'queued' }));

        toast({
          title: messages.genericToastTitleStarted,
          description: 'Started download of KML track',
        });

        const { queueUuid } = await queueKmlVesselTrack(session, mmsi);
        setDownloadState((prev) => ({ ...prev, status: 'downloading' }));

        const interval = setInterval(async () => {
          const status = await pollStatus(queueUuid, 'kml');
          if (status !== 'QueuePending') {
            clearPollingInterval();
          }
        }, POLLING_INTERVAL);
        pollingIntervalRef.current = interval;
      } catch (error) {
        handleError(error as Error);
      }
    },
    [
      session,
      reset,
      pollStatus,
      clearPollingInterval,
      handleError,
      toast,
      messages,
    ],
  );

  useEffect(() => {
    return () => {
      clearPollingInterval();
    };
  }, [clearPollingInterval]);

  return {
    downloadState,
    startCsvDownload,
    startKmlDownload,
    reset,
  };
};
